import {useCertificateConnectionsQuery} from './useCertificatesQuery';
import {CertificateConnection} from '../services/CertificatesService';

// FNP-1193 — WHICH CONNECTIONS THE REQUEST FORM MAY OFFER.
//
// ⚠️ THE SAME "NOT PERMITTED BY DEFAULT" RULE AS
// useCertificateWriteCapabilities, APPLIED ONE LEVEL DOWN.
//
// The Request button is gated on a connection that can write. The form then has
// to let the operator pick WHICH one — and if that picker offered every
// connection, a tenant would choose a read-only one and get a refusal at
// submit. That is precisely the failure the hiding rule exists to prevent,
// reintroduced inside the form.
//
// So the same two tests apply per connection, and both must PASS to be offered:
//   status === 'ACTIVE'   — the credential works NOW
//   writable              — and the tenant declared it may write
//
// ⚠️ `writable` IS THE BACKEND'S OWN GATE, not a DNS capability standing in for
// one. `flexi-domains-api/src/certificate/src/main.ts` derives `canRequest`
// from `connection.writable === true` and consults nothing else, so a
// connection failing this test is one the backend would refuse outright.
//
// ⚠️ PASSING BOTH IS STILL NOT A GUARANTEE. `writable` is declared by the
// tenant and cannot be probed, so a connection offered here may still be
// refused by ACM. The form says so at the point of submission rather than
// pretending this filter proved more than it did.

// Exported for its own test: the filter is the whole point of the hook, and
// testing it through React Query would test the plumbing instead of the rule.
export const canRequestThrough = (connection: CertificateConnection): boolean => {
    // A connection that is not ACTIVE declares nothing usable whatever it said
    // at connect time — a non-ACTIVE status is the provider saying the
    // credential does not work now.
    if (connection.status !== 'ACTIVE') {
        return false;
    }
    // Explicitly `=== true`, not truthy: an absent value must not pass.
    return connection.writable === true;
};

export const useCertificateRequestConnections = () => {

    const {
        data,
        isLoading,
        isError,
    } = useCertificateConnectionsQuery();

    const connections = data ?? [];
    const usable = connections.filter(canRequestThrough);

    return {
        connections: usable,
        loading: isLoading,
        isError,
        // ⚠️ DISTINCT FROM `usable.length === 0`, and the form needs both.
        //
        // "You have no connections" and "none of your connections can write
        // DNS" are different problems with different fixes — connect a
        // provider, versus reconnect one with a credential that can write. One
        // message for both sends half the tenants to the wrong place.
        hasAnyConnection: connections.length > 0,
    };
};
